/*
  Desktop full-width search dropdown panel (≥990px). A non-modal popover:
  it doesn't lock scroll or trap focus; outside click / Escape close it.
  Best sellers are fetched on first open (window.IceboxSearchBestSellers,
  defined in mobile-search-drawer.js).
  The class lives inside the registration guard so re-executing this script
  — e.g. on a theme editor header section reload — cannot throw a
  duplicate-declaration SyntaxError or a customElements.define
  NotSupportedError.
*/
if (!customElements.get('desktop-search-dropdown')) {
  customElements.define(
    'desktop-search-dropdown',
    class DesktopSearchDropdown extends HTMLElement {
      constructor() {
        super();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onResize = this.onResize.bind(this);
        this.resetHeaderHeight = this.resetHeaderHeight.bind(this);
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.trigger = this.querySelector('.desktop-search-dropdown__trigger');
        this.panel = this.querySelector('.desktop-search-dropdown__panel');
        this.closeButton = this.querySelector('.desktop-search-dropdown__close');
        this.input = this.querySelector('.nav-search__input');
        this.predictiveSearch = this.querySelector('predictive-search');

        if (this.panel) this.panel.hidden = true;

        this.trigger?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.open();
        });
        this.closeButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        });

        this.input?.addEventListener('focus', () => {
          if (!this.isOpen()) this.open(false);
        });

        this.resetHeaderHeight();
        requestAnimationFrame(() => {
          requestAnimationFrame(this.resetHeaderHeight);
        });
      }

      disconnectedCallback() {
        // Don't leave document listeners or the html-level open state behind
        // if the header section is re-rendered while the panel is open.
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('resize', this.onResize);
        document.documentElement.classList.remove('desktop-search-open');
      }

      resetHeaderHeight() {
        // Defer to the single shared writer in header.liquid so --header-height
        // has one source of truth and consistent timing (D-27).
        if (typeof window.__setHeaderHeight === 'function') {
          window.__setHeaderHeight();
          return;
        }
        const header = document.querySelector('.section-header');
        if (!header) return;
        document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
      }

      isOpen() {
        return this.classList.contains('desktop-search-dropdown--open');
      }

      updatePanelPosition() {
        const header = document.querySelector('.section-header');
        if (!header || !this.panel) return;
        this.panel.style.setProperty('--desktop-search-top', `${header.getBoundingClientRect().top}px`);
      }

      open(focusInput = true) {
        if (window.matchMedia('(max-width: 989px)').matches) return;
        if (!this.panel) return;

        this.updatePanelPosition();
        this.panel.hidden = false;
        window.IceboxSearchBestSellers?.load();
        this.classList.add('desktop-search-dropdown--open');
        document.documentElement.classList.add('desktop-search-open');
        this.trigger?.setAttribute('aria-expanded', 'true');

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('resize', this.onResize);

        if (focusInput) {
          window.requestAnimationFrame(() => this.input?.focus());
        }
      }

      close() {
        if (!this.isOpen()) return;

        this.panel.hidden = true;
        this.classList.remove('desktop-search-dropdown--open');
        document.documentElement.classList.remove('desktop-search-open');
        this.trigger?.setAttribute('aria-expanded', 'false');

        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('resize', this.onResize);

        if (this.predictiveSearch?.close) {
          this.predictiveSearch.close(true);
        } else if (this.input) {
          this.input.value = '';
        }

        this.trigger?.focus();
        this.resetHeaderHeight();
      }

      onDocumentClick(event) {
        if (!this.isOpen()) return;
        if (this.contains(event.target)) return;
        this.close();
      }

      onKeyDown(event) {
        if (!this.isOpen() || event.key !== 'Escape' || event.defaultPrevented) return;
        event.preventDefault();
        this.close();
      }

      onResize() {
        if (!this.isOpen()) return;
        this.updatePanelPosition();
      }
    }
  );
}
